import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  DAYS,
  STARTER_TAGS,
  VENUE_LABELS,
  addDays,
  can,
  type Game,
  type GameVenue,
  type Phase,
  type SeasonDetail,
  type SeasonSummary,
  type TeamDetail,
  type TemplateSummary,
  type TrainingSlot,
} from '@hoopify/shared';
import { gameResult, RESULT_TONE } from '../../lib/games';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { formatDayMonth, formatMinutes, formatShortDay, formatTime, today } from '../../lib/dates';
import { newId } from '../../lib/plan';
import { Alert, Button, Dialog, Empty, Field, LoadingBlock, Section, TextArea } from '../ui';
import { SeasonAttendance } from '../roster/SeasonAttendance';
import { TimeField, TimeInput } from '../TimeField';

const PHASE_HUES = [210, 150, 30, 280, 0, 180];
const phaseStyle = (i: number) => ({ backgroundColor: `hsl(${PHASE_HUES[i % PHASE_HUES.length]} 60% 50% / 0.14)`, color: `hsl(${PHASE_HUES[i % PHASE_HUES.length]} 45% 40%)` });
const phaseStyleDark = (i: number) => ({ backgroundColor: `hsl(${PHASE_HUES[i % PHASE_HUES.length]} 60% 60% / 0.18)`, color: `hsl(${PHASE_HUES[i % PHASE_HUES.length]} 70% 75%)` });
const isDark = () => document.documentElement.dataset.theme === 'dark';

/** A team's season: phases, a focus for each week, training times, games and what's been covered. */
export function SeasonTab({ team }: { team: TeamDetail }) {
  const [params, setParams] = useSearchParams();
  const list = useQuery({ queryKey: ['seasons', team.id], queryFn: () => api.get<SeasonSummary[]>(`/teams/${team.id}/seasons`) });
  const [creating, setCreating] = useState(false);
  const canEdit = can(team.role, 'sessions:write');

  if (list.isLoading) return <LoadingBlock />;
  if (list.isError) return <Alert>{errorMessage(list.error)}</Alert>;
  const seasons = list.data ?? [];
  const chosen = seasons.find((s) => s.id === params.get('season')) ?? seasons.find((s) => s.startDate <= today() && today() <= s.endDate) ?? seasons[0];

  if (!chosen) {
    return (
      <>
        <Empty title="No season set up yet" action={canEdit ? <Button onClick={() => setCreating(true)}>Set up a season</Button> : undefined}>
          A season gives you a week-by-week plan: phases like pre-season and finals, a focus for each week, your regular training times and your games.
        </Empty>
        <NewSeasonDialog teamId={team.id} open={creating} onClose={() => setCreating(false)} onCreated={(id) => setParams({ tab: 'season', season: id }, { replace: true })} />
      </>
    );
  }

  return (
    <>
      {seasons.length > 1 && (
        <label className="mb-4 flex items-center gap-2 text-[14px]">
          <span className="text-muted">Season</span>
          <select className="field-input w-auto py-1.5" value={chosen.id} onChange={(e) => setParams({ tab: 'season', season: e.target.value }, { replace: true })}>
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <SeasonView key={chosen.id} teamId={team.id} seasonId={chosen.id} onNew={canEdit ? () => setCreating(true) : undefined} />
      <NewSeasonDialog teamId={team.id} open={creating} onClose={() => setCreating(false)} onCreated={(id) => setParams({ tab: 'season', season: id }, { replace: true })} />
    </>
  );
}

function NewSeasonDialog({ teamId, open, onClose, onCreated }: { teamId: string; open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [startDate, setStart] = useState(today());
  const [endDate, setEnd] = useState(addDays(today(), 7 * 16 - 1));
  const create = useMutation({
    mutationFn: () => api.post<SeasonDetail>(`/teams/${teamId}/seasons`, { name, startDate, endDate }),
    onSuccess: async (s) => {
      await qc.invalidateQueries({ queryKey: ['seasons', teamId] });
      onCreated(s.id);
      onClose();
    },
  });
  const f = fieldErrors(create.error);
  return (
    <Dialog open={open} onClose={onClose} title="Set up a season">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }} className="space-y-4" noValidate>
        {create.isError && !Object.keys(f).length && <Alert>{errorMessage(create.error)}</Alert>}
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} error={f.name} placeholder="e.g. Summer 2026–27" autoFocus />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts" type="date" value={startDate} onChange={(e) => setStart(e.target.value)} error={f.startDate} />
          <Field label="Ends" type="date" value={endDate} onChange={(e) => setEnd(e.target.value)} error={f.endDate} />
        </div>
        <p className="text-[13px] text-muted">We’ll start you off with pre-season, competition and finals phases. You can change them.</p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Set up season
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- One season ----------

function SeasonView({ teamId, seasonId, onNew }: { teamId: string; seasonId: string; onNew?: () => void }) {
  const qc = useQueryClient();
  const key = ['season', seasonId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<SeasonDetail>(`/teams/${teamId}/seasons/${seasonId}`) });
  const [dialog, setDialog] = useState<'details' | 'phases' | 'training' | 'generate' | 'delete' | 'game' | null>(null);
  const [editingGame, setEditingGame] = useState<Game | null>(null);

  const patch = async (body: Record<string, unknown>) => {
    const next = await api.patch<SeasonDetail>(`/teams/${teamId}/seasons/${seasonId}`, body);
    qc.setQueryData(key, next);
    void qc.invalidateQueries({ queryKey: ['seasons', teamId] });
    return next;
  };

  if (q.isLoading) return <LoadingBlock />;
  if (q.isError || !q.data) return <Alert>{errorMessage(q.error)}</Alert>;
  const s = q.data;
  const phaseIndex = new Map(s.phases.map((p, i) => [p.id, i]));
  const styleFor = (id: string) => (isDark() ? phaseStyleDark : phaseStyle)(phaseIndex.get(id) ?? 0);

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[20px] font-semibold">{s.name}</h2>
          <p className="tnum mt-0.5 text-muted">
            {formatShortDay(s.startDate)} to {formatShortDay(s.endDate)}, {s.weeks.length} weeks
          </p>
          {s.phases.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {s.phases.map((p) => (
                <span key={p.id} className="tnum rounded px-2 py-0.5 text-[12px] font-medium" style={styleFor(p.id)}>
                  {p.name}: {formatDayMonth(p.start)} – {formatDayMonth(p.end)}
                </span>
              ))}
            </div>
          )}
        </div>
        {s.canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setDialog('details')}>
              <Pencil className="size-4" aria-hidden /> Dates
            </Button>
            <Button variant="secondary" onClick={() => setDialog('phases')}>
              Phases
            </Button>
            {onNew && (
              <Button variant="secondary" onClick={onNew}>
                <Plus className="size-4" aria-hidden /> New season
              </Button>
            )}
          </div>
        )}
      </header>

      <Section
        title="Training times"
        description="Your regular weekly sessions. Hoopify can create draft sessions for every week, so you only have to fill in the plans."
        aside={
          s.canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setDialog('training')}>
                {s.training.length ? 'Change times' : 'Add training times'}
              </Button>
              {s.training.length > 0 && (
                <Button onClick={() => setDialog('generate')}>
                  <CalendarPlus className="size-4" aria-hidden /> Create sessions
                </Button>
              )}
            </div>
          )
        }
      >
        {s.training.length ? (
          <ul className="flex flex-wrap gap-2">
            {s.training.map((t) => (
              <li key={t.id} className="tnum rounded-md border border-line bg-surface px-3 py-2 text-[14px]">
                <span className="font-medium">{DAYS[t.day - 1]}</span> {formatTime(t.startTime)}, {formatMinutes(t.durationMinutes)}
                {t.location ? `, ${t.location}` : ''}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">No regular training times yet.</p>
        )}
      </Section>

      <WeekPlan season={s} styleFor={styleFor} onSave={(weeks) => patch({ weeks })} />

      <Section title="What you’ve worked on" description="Minutes of drills by skill, across this season’s sessions. Drills running at the same time each count.">
        <CoverageBars season={s} />
      </Section>

      <Section title="Attendance" description="Who came to each session this season. Rates count late and sitting out as attending.">
        <SeasonAttendance teamId={teamId} seasonId={seasonId} />
      </Section>

      <Section
        title="Games"
        aside={
          s.canEdit && (
            <Button
              variant="secondary"
              onClick={() => {
                setEditingGame(null);
                setDialog('game');
              }}
            >
              <Plus className="size-4" aria-hidden /> Add game
            </Button>
          )
        }
      >
        <GameList
          games={s.weeks.flatMap((w) => w.games)}
          onEdit={
            s.canEdit
              ? (g) => {
                  setEditingGame(g);
                  setDialog('game');
                }
              : undefined
          }
        />
      </Section>

      {s.canEdit && (
        <Section title="Delete season" description="Removes the season plan. Sessions and games stay on the schedule.">
          <Button variant="danger" onClick={() => setDialog('delete')}>
            <Trash2 className="size-4" aria-hidden /> Delete {s.name}
          </Button>
        </Section>
      )}

      {dialog === 'details' && <DetailsDialog season={s} onSave={patch} onClose={() => setDialog(null)} />}
      {dialog === 'phases' && <PhasesDialog season={s} onSave={patch} onClose={() => setDialog(null)} />}
      {dialog === 'training' && <TrainingDialog season={s} onSave={patch} onClose={() => setDialog(null)} />}
      {dialog === 'generate' && <GenerateDialog season={s} teamId={teamId} onClose={() => setDialog(null)} />}
      {dialog === 'game' && <GameDialog teamId={teamId} seasonId={seasonId} game={editingGame} onClose={() => setDialog(null)} />}
      {dialog === 'delete' && <DeleteSeasonDialog season={s} teamId={teamId} onClose={() => setDialog(null)} />}
    </div>
  );
}

// ---------- Week by week ----------

function WeekPlan({ season: s, styleFor, onSave }: { season: SeasonDetail; styleFor: (id: string) => React.CSSProperties; onSave: (weeks: { weekStart: string; focus: string | null; tags: string[] }[]) => Promise<unknown> }) {
  const initial = useMemo(() => Object.fromEntries(s.weeks.map((w) => [w.weekStart, { focus: w.focus ?? '', tags: w.tags }])), [s.weeks]);
  const [draft, setDraft] = useState(initial);
  useEffect(() => setDraft(initial), [initial]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const save = useMutation({ mutationFn: () => onSave(Object.entries(draft).map(([weekStart, v]) => ({ weekStart, focus: v.focus.trim() || null, tags: v.tags }))) });
  const set = (ws: string, v: Partial<{ focus: string; tags: string[] }>) => setDraft((d) => ({ ...d, [ws]: { ...d[ws]!, ...v } }));
  const thisWeek = s.weeks.find((w) => w.weekStart <= today() && today() <= addDays(w.weekStart, 6))?.weekStart;

  return (
    <Section
      title="Week by week"
      description="Set a focus for each week. New sessions created from your training times pick up the week’s focus."
      aside={
        s.canEdit && (
          <div className="flex items-center gap-3">
            {save.isSuccess && !dirty && <span className="text-[14px] text-ok">Saved</span>}
            <Button onClick={() => save.mutate()} disabled={!dirty} loading={save.isPending}>
              Save weeks
            </Button>
          </div>
        )
      }
    >
      {save.isError && <div className="mb-3"><Alert>{errorMessage(save.error)}</Alert></div>}
      <ol className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
        {s.weeks.map((w, i) => {
          const v = draft[w.weekStart] ?? { focus: '', tags: [] };
          const minutes = w.sessions.reduce((n, x) => n + x.plannedMinutes, 0);
          return (
            <li key={w.weekStart} className={`grid gap-x-4 gap-y-2 px-4 py-3 md:grid-cols-[132px_minmax(0,1fr)_minmax(0,240px)] ${w.weekStart === thisWeek ? 'bg-action-soft/40' : ''}`}>
              <div>
                <p className="tnum text-[14px] font-semibold">
                  Week {i + 1}
                  {w.weekStart === thisWeek && <span className="ml-1.5 text-[12px] font-medium text-action">this week</span>}
                </p>
                <p className="tnum text-[13px] text-muted">
                  {formatDayMonth(w.weekStart)} – {formatDayMonth(addDays(w.weekStart, 6))}
                </p>
                {w.phase && (
                  <span className="mt-1 inline-block rounded px-1.5 py-0.5 text-[11px] font-medium" style={styleFor(w.phase.id)}>
                    {w.phase.name}
                  </span>
                )}
              </div>
              <div className="min-w-0 space-y-2">
                {s.canEdit ? (
                  <input
                    className="field-input py-1.5"
                    value={v.focus}
                    maxLength={200}
                    onChange={(e) => set(w.weekStart, { focus: e.target.value })}
                    placeholder="Focus for the week, e.g. transition defence"
                    aria-label={`Focus for week ${i + 1}`}
                  />
                ) : (
                  <p className={v.focus ? '' : 'text-muted'}>{v.focus || 'No focus set'}</p>
                )}
                <WeekTags value={v.tags} readOnly={!s.canEdit} onChange={(tags) => set(w.weekStart, { tags })} />
              </div>
              <div className="space-y-1 text-[13px]">
                {w.sessions.map((x) => (
                  <Link key={x.id} to={`/teams/${s.teamId}/sessions/${x.id}`} className="flex items-baseline justify-between gap-2 rounded px-1.5 py-0.5 hover:bg-sunken">
                    <span className="truncate">
                      <span className="tnum text-muted">{formatShortDay(x.date).split(' ')[0]}</span> {x.title}
                      {x.status === 'draft' && <span className="text-muted"> (draft)</span>}
                    </span>
                    <span className="tnum shrink-0 text-muted">{x.plannedMinutes ? `${x.plannedMinutes} min` : 'empty'}</span>
                  </Link>
                ))}
                {w.games.map((g) => (
                  <p key={g.id} className="px-1.5 font-medium">
                    <span className="tnum font-normal text-muted">{formatShortDay(g.date).split(' ')[0]}</span> Game vs {g.opponent}{' '}
                    <span className="font-normal text-muted">({VENUE_LABELS[g.venue].toLowerCase()})</span>
                  </p>
                ))}
                {!w.sessions.length && !w.games.length && <p className="px-1.5 text-muted">No sessions</p>}
                {minutes > 0 && <p className="tnum px-1.5 text-faint">{formatMinutes(minutes)} planned</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </Section>
  );
}

function WeekTags({ value, onChange, readOnly }: { value: string[]; onChange: (t: string[]) => void; readOnly?: boolean }) {
  const left = STARTER_TAGS.filter((t) => !value.includes(t));
  if (readOnly && !value.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {value.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded-full bg-action-soft px-2 py-0.5 text-[12px] text-action">
          {t}
          {!readOnly && (
            <button onClick={() => onChange(value.filter((x) => x !== t))} aria-label={`Remove ${t}`} className="hover:text-ink">
              <X className="size-3" />
            </button>
          )}
        </span>
      ))}
      {!readOnly && value.length < 6 && (
        <select
          className="rounded-full border border-dashed border-line-strong bg-transparent px-2 py-0.5 text-[12px] text-muted"
          value=""
          onChange={(e) => e.target.value && onChange([...value, e.target.value])}
          aria-label="Add a skill"
        >
          <option value="">+ Skill</option>
          {left.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

// ---------- Coverage ----------

function CoverageBars({ season: s }: { season: SeasonDetail }) {
  const c = s.coverage;
  if (!c.totalMinutes) return <p className="text-muted">Once sessions have drills in them, you’ll see how much time went to each skill.</p>;
  const focusTags = new Set(s.weeks.flatMap((w) => w.tags.map((t) => t.toLowerCase())));
  const rows = [...c.byTag, ...(c.untaggedMinutes ? [{ tag: 'No skill tagged', minutes: c.untaggedMinutes }] : [])];
  const max = Math.max(...rows.map((r) => r.minutes));
  const missing = [...focusTags].filter((t) => !c.byTag.some((b) => b.tag.toLowerCase() === t));
  return (
    <div className="max-w-[640px]">
      <p className="tnum mb-3 text-[14px] text-muted">
        {formatMinutes(c.totalMinutes)} of drills across {c.sessionCount} planned {c.sessionCount === 1 ? 'session' : 'sessions'}.
      </p>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.tag} className="grid grid-cols-[120px_minmax(0,1fr)_64px] items-center gap-3 text-[14px]">
            <span className={`truncate ${r.tag === 'No skill tagged' ? 'text-muted' : ''}`}>{r.tag}</span>
            <span className="h-3 overflow-hidden rounded-sm bg-sunken" aria-hidden>
              <span className={`block h-full rounded-sm ${r.tag === 'No skill tagged' ? 'bg-line-strong' : 'bg-brand-gradient'}`} style={{ width: `${(r.minutes / max) * 100}%` }} />
            </span>
            <span className="tnum text-right text-muted">{formatMinutes(r.minutes)}</span>
          </li>
        ))}
      </ul>
      {missing.length > 0 && (
        <p className="mt-3 text-[14px] text-warn">
          Weekly focus includes {missing.join(', ')}, but no planned drills are tagged with {missing.length === 1 ? 'it' : 'them'} yet.
        </p>
      )}
    </div>
  );
}

// ---------- Games ----------

function GameList({ games, onEdit }: { games: Game[]; onEdit?: (g: Game) => void }) {
  if (!games.length) return <p className="text-muted">No games in this season yet.</p>;
  return (
    <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
      {games.map((g) => (
        <li key={g.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
          <span className="tnum w-[150px] text-[14px] text-muted">
            {formatShortDay(g.date)}
            {g.startTime ? `, ${formatTime(g.startTime)}` : ''}
          </span>
          <span className="min-w-0 flex-1">
            <span className="font-medium">vs {g.opponent}</span>{' '}
            <span className="text-[13px] text-muted">
              {g.round ? `${g.round}, ` : ''}
              {VENUE_LABELS[g.venue]}
              {g.location ? `, ${g.location}` : ''}
            </span>
            {(() => {
              const r = gameResult(g);
              return r ? <span className={`ml-2 text-[13px] font-semibold ${RESULT_TONE[r.tone]}`}>{r.text}</span> : null;
            })()}
          </span>
          {g.fromPlayhq && <span className="rounded-full bg-sunken px-2 py-0.5 text-[11px] font-medium text-muted" title="Kept up to date from PlayHQ">PlayHQ</span>}
          {onEdit && (
            <button className="btn-ghost px-2 py-1 text-[13px]" onClick={() => onEdit(g)}>
              Edit
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function GameDialog({ teamId, seasonId, game, onClose }: { teamId: string; seasonId: string; game: Game | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState({
    date: game?.date ?? today(),
    startTime: game?.startTime ?? '',
    opponent: game?.opponent ?? '',
    venue: (game?.venue ?? 'home') as GameVenue,
    location: game?.location ?? '',
    notes: game?.notes ?? '',
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['season', seasonId] });
    void qc.invalidateQueries({ queryKey: ['schedule-games'] });
  };
  const save = useMutation({
    mutationFn: () => {
      const body = { ...v, startTime: v.startTime || null };
      return game ? api.patch(`/teams/${teamId}/games/${game.id}`, body) : api.post(`/teams/${teamId}/games`, body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/teams/${teamId}/games/${game!.id}`),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const f = fieldErrors(save.error);
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  if (game?.fromPlayhq) {
    const r = gameResult(game);
    return (
      <Dialog open onClose={onClose} title={`vs ${game.opponent}`}>
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[14px]">
            <dt className="text-muted">When</dt>
            <dd>{formatShortDay(game.date)}{game.startTime ? `, ${formatTime(game.startTime)}` : ''}</dd>
            {game.round && (<><dt className="text-muted">Round</dt><dd>{game.round}</dd></>)}
            <dt className="text-muted">Where</dt>
            <dd>{VENUE_LABELS[game.venue]}{game.location ? `, ${game.location}` : ''}</dd>
            {r && (<><dt className="text-muted">Result</dt><dd className={`font-semibold ${RESULT_TONE[r.tone]}`}>{r.text}</dd></>)}
          </dl>
          <Alert tone="info">This game comes from PlayHQ and stays in step with the fixture, so only the notes can be changed here.</Alert>
          <TextArea label="Notes (optional)" value={v.notes} onChange={set('notes')} error={f.notes} maxLength={1000} hint="Everyone on the team can see games and their notes." autoFocus />
          {save.isError && !Object.keys(f).length && <Alert>{errorMessage(save.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={save.isPending}>Save notes</Button>
          </div>
        </form>
      </Dialog>
    );
  }
  return (
    <Dialog open onClose={onClose} title={game ? 'Edit game' : 'Add a game'}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
        {(save.isError || remove.isError) && !Object.keys(f).length && <Alert>{errorMessage(save.error ?? remove.error)}</Alert>}
        <Field label="Opponent" value={v.opponent} onChange={set('opponent')} error={f.opponent} maxLength={80} autoFocus />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date" type="date" value={v.date} onChange={set('date')} error={f.date} />
          <TimeField label="Time (optional)" value={v.startTime} onChange={(t) => set('startTime')({ target: { value: t } })} error={f.startTime} />
        </div>
        <fieldset>
          <legend className="text-[14px] font-medium">Where</legend>
          <div className="mt-1.5 grid grid-cols-3 gap-1 rounded-md bg-sunken p-1 text-[14px]">
            {(['home', 'away', 'neutral'] as const).map((x) => (
              <button type="button" key={x} onClick={() => setV({ ...v, venue: x })} aria-pressed={v.venue === x} className={`rounded py-1 font-medium ${v.venue === x ? 'bg-surface text-ink shadow-sm' : 'text-muted'}`}>
                {VENUE_LABELS[x]}
              </button>
            ))}
          </div>
        </fieldset>
        <Field label="Venue (optional)" value={v.location} onChange={set('location')} error={f.location} maxLength={120} />
        <TextArea label="Notes (optional)" value={v.notes} onChange={set('notes')} error={f.notes} maxLength={1000} hint="Everyone on the team can see games and their notes." />
        <div className="flex flex-wrap justify-end gap-2">
          {game && (
            <Button type="button" variant="danger" className="btn-danger mr-auto" loading={remove.isPending} onClick={() => remove.mutate()}>
              Remove game
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            {game ? 'Save game' : 'Add game'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Dialogs ----------

function DetailsDialog({ season: s, onSave, onClose }: { season: SeasonDetail; onSave: (b: Record<string, unknown>) => Promise<unknown>; onClose: () => void }) {
  const [v, setV] = useState({ name: s.name, startDate: s.startDate, endDate: s.endDate });
  const save = useMutation({ mutationFn: () => onSave(v), onSuccess: onClose });
  const f = fieldErrors(save.error);
  return (
    <Dialog open onClose={onClose} title="Season dates">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
        {save.isError && !Object.keys(f).length && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} error={f.name} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts" type="date" value={v.startDate} onChange={(e) => setV({ ...v, startDate: e.target.value })} error={f.startDate} />
          <Field label="Ends" type="date" value={v.endDate} onChange={(e) => setV({ ...v, endDate: e.target.value })} error={f.endDate} />
        </div>
        <p className="text-[13px] text-muted">Changing the dates doesn’t move phases. Check them afterwards.</p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function PhasesDialog({ season: s, onSave, onClose }: { season: SeasonDetail; onSave: (b: Record<string, unknown>) => Promise<unknown>; onClose: () => void }) {
  const [phases, setPhases] = useState<Phase[]>(s.phases);
  const save = useMutation({ mutationFn: () => onSave({ phases }), onSuccess: onClose });
  const set = (id: string, c: Partial<Phase>) => setPhases(phases.map((p) => (p.id === id ? { ...p, ...c } : p)));
  return (
    <Dialog open onClose={onClose} title="Phases" size="lg">
      <p className="mb-4 text-muted">Blocks of the season with their own emphasis, like pre-season, competition and finals.</p>
      {save.isError && <div className="mb-3"><Alert>{errorMessage(save.error)}</Alert></div>}
      <ul className="space-y-2">
        {phases.map((p) => (
          <li key={p.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 sm:grid-cols-[minmax(0,1fr)_150px_150px_auto] sm:items-center">
            <input className="field-input" value={p.name} maxLength={40} onChange={(e) => set(p.id, { name: e.target.value })} aria-label="Phase name" />
            <button className="btn-ghost px-2 sm:order-last" onClick={() => setPhases(phases.filter((x) => x.id !== p.id))} aria-label={`Remove ${p.name}`}>
              <Trash2 className="size-4" />
            </button>
            <input className="field-input" type="date" value={p.start} onChange={(e) => set(p.id, { start: e.target.value })} aria-label={`${p.name} starts`} />
            <input className="field-input" type="date" value={p.end} onChange={(e) => set(p.id, { end: e.target.value })} aria-label={`${p.name} ends`} />
          </li>
        ))}
      </ul>
      <Button variant="secondary" className="btn-secondary mt-3" onClick={() => setPhases([...phases, { id: newId(), name: 'New phase', start: s.startDate, end: s.endDate }])}>
        <Plus className="size-4" aria-hidden /> Add phase
      </Button>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Save phases
        </Button>
      </div>
    </Dialog>
  );
}

function TrainingDialog({ season: s, onSave, onClose }: { season: SeasonDetail; onSave: (b: Record<string, unknown>) => Promise<unknown>; onClose: () => void }) {
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api.get<TemplateSummary[]>('/templates') });
  const [slots, setSlots] = useState<TrainingSlot[]>(s.training.length ? s.training : [{ id: newId(), day: 2, startTime: '17:00', durationMinutes: 90, location: null, templateId: null }]);
  const save = useMutation({ mutationFn: () => onSave({ training: slots }), onSuccess: onClose });
  const set = (id: string, c: Partial<TrainingSlot>) => setSlots(slots.map((x) => (x.id === id ? { ...x, ...c } : x)));
  return (
    <Dialog open onClose={onClose} title="Training times" size="lg">
      <p className="mb-4 text-muted">When the team usually trains each week. Optionally start each session from one of your templates.</p>
      {save.isError && <div className="mb-3"><Alert>{errorMessage(save.error)}</Alert></div>}
      <ul className="space-y-3">
        {slots.map((t) => (
          <li key={t.id} className="grid grid-cols-2 gap-2 rounded-md border border-line p-3 sm:grid-cols-[140px_120px_90px_minmax(0,1fr)_auto] sm:items-end sm:border-0 sm:p-0">
            <label className="space-y-1">
              <span className="block text-[12px] text-muted">Day</span>
              <select className="field-input py-1.5" value={t.day} onChange={(e) => set(t.id, { day: Number(e.target.value) })}>
                {DAYS.map((d, i) => (
                  <option key={d} value={i + 1}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="block text-[12px] text-muted">Starts</span>
              <TimeInput className="py-1.5" value={t.startTime} onChange={(v) => set(t.id, { startTime: v })} aria-label="Starts" />
            </label>
            <label className="space-y-1">
              <span className="block text-[12px] text-muted">Minutes</span>
              <input className="field-input tnum py-1.5" type="number" min={10} max={360} step={5} value={t.durationMinutes} onChange={(e) => set(t.id, { durationMinutes: Number(e.target.value) })} />
            </label>
            <label className="space-y-1">
              <span className="block text-[12px] text-muted">Start from</span>
              <select className="field-input py-1.5" value={t.templateId ?? ''} onChange={(e) => set(t.id, { templateId: e.target.value || null })}>
                <option value="">A blank plan</option>
                {(templates.data ?? []).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.title}
                  </option>
                ))}
              </select>
            </label>
            <button className="btn-ghost justify-self-start px-2" onClick={() => setSlots(slots.filter((x) => x.id !== t.id))} aria-label="Remove this training time">
              <Trash2 className="size-4" />
            </button>
            <label className="col-span-2 space-y-1 sm:col-span-4">
              <span className="block text-[12px] text-muted">Location (optional)</span>
              <input className="field-input py-1.5" maxLength={120} value={t.location ?? ''} onChange={(e) => set(t.id, { location: e.target.value || null })} />
            </label>
          </li>
        ))}
      </ul>
      <Button variant="secondary" className="btn-secondary mt-3" onClick={() => setSlots([...slots, { id: newId(), day: 4, startTime: '17:00', durationMinutes: 90, location: null, templateId: null }])}>
        <Plus className="size-4" aria-hidden /> Add a time
      </Button>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Save times
        </Button>
      </div>
    </Dialog>
  );
}

function GenerateDialog({ season: s, teamId, onClose }: { season: SeasonDetail; teamId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [from, setFrom] = useState(today() > s.startDate ? today() : s.startDate);
  const url = `/teams/${teamId}/seasons/${s.id}/generate`;
  const preview = useQuery({
    queryKey: ['generate-preview', s.id, from],
    queryFn: () => api.post<{ created: number; skipped: number; dates: string[] }>(url, { from, dryRun: true }),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(from),
  });
  const run = useMutation({
    mutationFn: () => api.post<{ created: number }>(url, { from }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['season', s.id] });
      void qc.invalidateQueries({ queryKey: ['schedule'] });
      void qc.invalidateQueries({ queryKey: ['team-sessions'] });
    },
  });
  return (
    <Dialog open onClose={onClose} title="Create sessions">
      {run.isSuccess ? (
        <div className="space-y-4">
          <Alert tone="ok">
            Created {run.data.created} draft {run.data.created === 1 ? 'session' : 'sessions'}. Fill in each plan, then publish them to the team.
          </Alert>
          <div className="flex justify-end">
            <Button onClick={onClose}>Done</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-muted">Creates a draft for each of your training times, every week until {formatShortDay(s.endDate)}. Days that already have a session at that time are skipped. Nothing is published.</p>
          <Field label="Starting from" type="date" value={from} min={s.startDate} max={s.endDate} onChange={(e) => setFrom(e.target.value)} />
          {preview.isError && <Alert>{errorMessage(preview.error)}</Alert>}
          {run.isError && <Alert>{errorMessage(run.error)}</Alert>}
          {preview.data && (
            <p className="tnum">
              {preview.data.created ? (
                <>
                  This will create <strong>{preview.data.created}</strong> draft {preview.data.created === 1 ? 'session' : 'sessions'}
                  {preview.data.dates.length ? `, from ${formatShortDay(preview.data.dates[0]!)} to ${formatShortDay(preview.data.dates[preview.data.dates.length - 1]!)}` : ''}.
                </>
              ) : (
                'There’s nothing new to create: every training time already has a session.'
              )}
              {preview.data.skipped > 0 && ` ${preview.data.skipped} already have a session.`}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={run.isPending} disabled={!preview.data?.created} onClick={() => run.mutate()}>
              Create {preview.data?.created ?? ''} sessions
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function DeleteSeasonDialog({ season: s, teamId, onClose }: { season: SeasonDetail; teamId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [, setParams] = useSearchParams();
  const remove = useMutation({
    mutationFn: () => api.del(`/teams/${teamId}/seasons/${s.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['seasons', teamId] });
      setParams({ tab: 'season' }, { replace: true });
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} title={`Delete ${s.name}?`}>
      <p className="text-muted">The phases, weekly focus and training times go. Sessions and games stay on the schedule.</p>
      {remove.isError && <div className="mt-3"><Alert>{errorMessage(remove.error)}</Alert></div>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Keep it
        </Button>
        <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
          Delete season
        </Button>
      </div>
    </Dialog>
  );
}
